import { useMutation } from "@tanstack/react-query";
import cx from "classnames";
import dayjs from "dayjs";
import { isEqual } from "es-toolkit/compat";
import { useEffect, useState } from "react";
import {
  ActionFunction,
  Form,
  LoaderFunction,
  redirect,
  useActionData,
  useLoaderData,
  useNavigate,
  useSubmit,
} from "react-router-dom";

import {
  deleteUser,
  getPlatform,
  getPlatformUserMemberships,
  getUser,
  postUserUpdate,
} from "api/methods";
import { PostRequest } from "api/types";
import { useTranslation } from "@/core/i18n";
import { Dropdown } from "atoms/dropdown/Dropdown";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import { TextButton } from "atoms/text-button/TextButton";
import { TextField } from "atoms/text-field/TextField";
import { ActionModal } from "molecules/modal-window/action-modal/ActionModal";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";
import { readFormData } from "toolbox/ReadFormData";
import { useLogout } from "utils/useLogout";
import { hasCancellationRunway } from "utils/membershipStatus";

type ActionResult = {
  saveSuccessTimestamp: number;
};

const load = async () => {
  const userInfo = queryClient.fetchQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });
  const platform = queryClient.fetchQuery({ ...getPlatform.keyed() });
  const memberships = queryClient.fetchQuery({ ...getPlatformUserMemberships.keyed() });

  return waitObj({ userInfo, platform, memberships });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const accountDetailsLoader: LoaderFunction = () => {
  return load();
};

export const accountDetailsAction: ActionFunction = async ({ request }) => {
  const data = await readFormData<
    PostRequest<"/user/update"> & { shouldGoBackOnSuccess?: boolean }
  >(request);

  await postUserUpdate(data);

  if (data.shouldGoBackOnSuccess) {
    return redirect("..");
  }

  queryClient.invalidateQueries({ queryKey: getUser.key() });

  return {
    saveSuccessTimestamp: Date.now(),
  };
};

export const AccountDetailsPage = () => {
  const { t } = useTranslation();
  const { userInfo, platform, memberships } = useLoaderData() as LoaderData;
  const submit = useSubmit();
  const actionData = useActionData() as ActionResult | undefined;
  const navigate = useNavigate();
  const logout = useLogout({ subdomain: platform?.subdomain });
  const [openModal, setOpenModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [hideSuccessfullySavedMessage, setHideSuccessfullySavedMessage] = useState(false);
  const savedUserInfo = {
    birthday: userInfo.dateOfBirth,
    firstName: userInfo.firstName || "",
    lastName: userInfo.lastName || "",
    phoneNumber: userInfo.phoneNumber || "",
    email: userInfo.email || "",
    gender: userInfo.gender ?? "",
  };
  const [formData, setFormData] = useState(savedUserInfo);

  useEffect(() => {
    if (actionData?.saveSuccessTimestamp) {
      setHideSuccessfullySavedMessage(false);
    }
  }, [actionData?.saveSuccessTimestamp]);

  useEffect(() => {
    if (actionData?.saveSuccessTimestamp && !hideSuccessfullySavedMessage) {
      const interval = setInterval(() => {
        setHideSuccessfullySavedMessage(true);
      }, 2000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [actionData?.saveSuccessTimestamp, hideSuccessfullySavedMessage]);

  const deleteAccountMutation = useMutation({
    mutationFn: async () => await deleteUser(undefined),
    onSuccess: async () => {
      setShowDeleteModal(false);
      logout();
    },
  });

  const hasActiveMembership = memberships.some(hasCancellationRunway);

  const hasChanges = !isEqual(savedUserInfo, formData);

  const submitForm = (shouldGoBackOnSuccess?: boolean) => () => {
    submit(
      {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        gender: formData.gender,
        ...(shouldGoBackOnSuccess ? { shouldGoBackOnSuccess: "true" } : {}),
      },
      { method: "post" },
    );
  };

  const dateOfBirth = userInfo.dateOfBirth ? dayjs(userInfo.dateOfBirth).format("L") : "-";

  const genderOptions = [
    { id: "female", label: t("accountDetails.genderWoman") },
    { id: "male", label: t("accountDetails.genderMan") },
    { id: "non-binary", label: t("accountDetails.genderNonBinary") },
    { id: "prefer-not-to-say", label: t("accountDetails.genderPreferNotToSay") },
  ];

  const getGenderLabel = (genderId: string) => {
    const option = genderOptions.find((opt) => opt.id === genderId);
    return option?.label ?? "-";
  };

  return (
    <div className="flex h-full flex-col bg-one">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => {
          if (hasChanges) {
            setOpenModal(true);
          } else {
            navigate(-1);
          }
        }}
      >
        <div className="flex items-center text-sub2">{t("accountDetails.header")}</div>
        <div className={cx(!hasChanges && "invisible", "absolute right-3 pr-3")}>
          <TextButton onClick={submitForm()}>
            <div className="text-bold1">{t("common.save")}</div>
          </TextButton>
        </div>
      </TopNav>
      <div className="relative flex flex-1 flex-col">
        <div
          className={cx(
            "absolute left-2 right-2 top-5 z-10 rounded bg-black py-4 pl-6 text-body2 text-white",
            (!actionData?.saveSuccessTimestamp || hideSuccessfullySavedMessage) && "hidden",
          )}
        >
          {t("accountDetails.changesSaved")}
        </div>
        <div className="flex-grow">
          <div className="mb-4 mt-6 text-center text-body2 text-secondary">
            {t("accountDetails.selectFieldPrompt")}
          </div>

          <Form className="mx-6 flex flex-col gap-4">
            <TextField
              label={t("accountDetails.firstName")}
              value={formData.firstName}
              onChange={(value) => setFormData({ ...formData, firstName: value })}
            />
            <TextField
              label={t("accountDetails.lastName")}
              value={formData.lastName}
              onChange={(value) => setFormData({ ...formData, lastName: value })}
            />
            <TextField
              type="tel"
              label={t("accountDetails.phoneNumber")}
              disabled
              value={formData.phoneNumber}
            />
            <TextField
              type="email"
              label={t("accountDetails.email")}
              value={formData.email}
              onChange={(value) => setFormData({ ...formData, email: value })}
            />
            <Dropdown
              label={t("accountDetails.gender")}
              value={formData.gender}
              textValue={getGenderLabel(formData.gender)}
              options={genderOptions.map(({ id, label }) => ({
                id,
                name: label,
              }))}
              setValue={(value) => setFormData({ ...formData, gender: value })}
            />
            <TextField label={t("accountDetails.birthday")} disabled value={dateOfBirth} />
          </Form>
        </div>
        <div className="mt-auto flex w-full flex-col items-center justify-center p-4 pb-8">
          <TextButton
            onClick={() => setShowDeleteModal(true)}
            variant="secondary"
            disabled={hasActiveMembership}
          >
            <span className="text-body2 text-secondary">{t("accountDetails.deleteAccount")}</span>
          </TextButton>
          {hasActiveMembership && (
            <span className="mt-2 text-center text-body2 text-secondary">
              {t("accountDetails.cancelMembershipFirst")}
            </span>
          )}
        </div>
      </div>
      <ActionModal
        size="small"
        open={openModal}
        onClose={() => {
          setOpenModal(false);
        }}
        onFirstButtonClick={submitForm(true)}
        onSecondButtonClick={() => {
          navigate(-1);
        }}
        firstButtonLabel={t("common.saveChanges")}
        secondButtonLabel={t("accountDetails.discardChanges")}
        title={t("accountDetails.unsavedChangesTitle")}
        description={<div className="px-2">{t("accountDetails.unsavedChangesDesc")}</div>}
      />
      <ActionModal
        size="small"
        open={showDeleteModal}
        onClose={() => !deleteAccountMutation.isPending && setShowDeleteModal(false)}
        onFirstButtonClick={() => deleteAccountMutation.mutate()}
        firstButtonLabel={
          deleteAccountMutation.isPending
            ? t("accountDetails.deleting")
            : t("accountDetails.deleteMyAccount")
        }
        firstButtonDisabled={deleteAccountMutation.isPending}
        onSecondButtonClick={() => setShowDeleteModal(false)}
        secondButtonLabel={t("common.cancel")}
        secondButtonDisabled={deleteAccountMutation.isPending}
        title={t("accountDetails.deleteTitle")}
        description={
          <div className="text-body2 text-secondary">
            {deleteAccountMutation.isError ? (
              <span className="text-red-500">{t("accountDetails.deleteFailed")}</span>
            ) : (
              t("accountDetails.deleteWarning")
            )}
          </div>
        }
      />
    </div>
  );
};
